import React, { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Help() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [addinVersion, setAddinVersion] = React.useState<string>('1.4.3');
  const [downloadUrl, setDownloadUrl] = React.useState<string>('/downloads/addin/QuickQuizAddIn-Setup-v1.4.3.msi');

  React.useEffect(() => {
    // Fetch the current add-in version
    fetch('/downloads/addin/version.json')
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        const version = data.version || '1.4.3';
        const installer = data.installer || `QuickQuizAddIn-Setup-v${version}.msi`;
        setAddinVersion(version);
        setDownloadUrl(`/downloads/addin/${installer}`);
      })
      .catch(() => {
        // Fallback already set in state
      });
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "How do I create a quiz?",
      answer: "Click 'Host a Quiz' in the navigation bar, then choose either 'Create New Quiz' to build from scratch or 'Generate Random Trivia Quiz' for instant questions. You can edit questions, set time limits, and mark correct answers."
    },
    {
      question: "How do participants join my quiz?",
      answer: "After creating a quiz, you’ll see a quiz ID and join URL. Share these or the QR code. Participants enter their name and the quiz code; if sign-in is required, they’ll be prompted."
    },
    {
      question: "Can I edit a quiz after creating it?",
      answer: "Yes! You can edit quizzes that are in 'draft' state. Click 'Edit Quiz' on your quiz card in the Host page. Once a quiz has started or ended, it becomes view-only to preserve integrity."
    },
    {
      question: "How does scoring work?",
      answer: "Participants earn points based on both accuracy and speed. Correct answers earn between 600-1000 points. The faster you answer correctly, the more points you get! Wrong answers receive no points."
    },
    {
      question: "Can I use QuickQuiz in incognito/private mode?",
      answer: "Usually yes—if anonymous sign‑in is enabled. Otherwise, participants will be asked to sign in before joining."
    },
    {
      question: "How many participants can join a quiz?",
      answer: "QuickQuiz is designed for small to medium sessions. We recommend <100–200 participants for the best experience."
    },
    {
      question: "Can I download quiz results?",
      answer: "Yes. After a quiz ends, use the leaderboard buttons: “Download Results” (CSV) or “Download as PPT”."
    },
    {
      question: "What happens if I lose internet connection during a quiz?",
      answer: "Reconnect and rejoin using the same quiz link. You may miss a question while offline, but the session continues for everyone else."
    },
    {
      question: "How do I delete a quiz?",
      answer: "On the Host page, find your quiz and click the 'Delete' button. Note: This action is permanent and cannot be undone. All participant data and results will be deleted."
    },
    {
      question: "Can participants see the correct answers?",
      answer: "Yes—once the host reveals the answer or shows results. Participants then see the correct option highlighted."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Help & Documentation
          </h1>
          <p className="text-xl text-gray-600">
            Everything you need to know about QuickQuiz
          </p>
        </div>

        {/* Quick Start Guide */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-6 text-gray-800 flex items-center gap-2">
            <span>🚀</span> Quick Start Guide
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Host Guide */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
              <h3 className="text-2xl font-semibold text-blue-700 mb-4 flex items-center gap-2">
                <span>👨‍🏫</span> For Hosts
              </h3>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</span>
                  <div>
                    <p className="font-medium text-gray-800">Create Your Quiz</p>
                    <p className="text-sm text-gray-600">Click "Host a Quiz" and choose to create from scratch or generate random trivia</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</span>
                  <div>
                    <p className="font-medium text-gray-800">Customize Questions</p>
                    <p className="text-sm text-gray-600">Add questions, set time limits, and mark correct answers</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</span>
                  <div>
                    <p className="font-medium text-gray-800">Share Quiz Code</p>
                    <p className="text-sm text-gray-600">Share the quiz ID, URL, or QR code with participants</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">4</span>
                  <div>
                    <p className="font-medium text-gray-800">Begin Quiz</p>
                    <p className="text-sm text-gray-600">Click "Begin Quiz" and control the pace with Next Question</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">5</span>
                  <div>
                    <p className="font-medium text-gray-800">View Results</p>
                    <p className="text-sm text-gray-600">See live leaderboard and download results when finished</p>
                  </div>
                </li>
              </ol>
            </div>

            {/* Participant Guide */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6">
              <h3 className="text-2xl font-semibold text-purple-700 mb-4 flex items-center gap-2">
                <span>🎮</span> For Participants
              </h3>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</span>
                  <div>
                    <p className="font-medium text-gray-800">Navigate to Join</p>
                    <p className="text-sm text-gray-600">Click "Join a Quiz" in the navigation or scan the QR code</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</span>
                  <div>
                    <p className="font-medium text-gray-800">Enter Your Info</p>
                    <p className="text-sm text-gray-600">Type your name and the quiz code provided by the host (sign in if prompted)</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</span>
                  <div>
                    <p className="font-medium text-gray-800">Wait in Lobby</p>
                    <p className="text-sm text-gray-600">See other participants joining and wait for the host to start</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">4</span>
                  <div>
                    <p className="font-medium text-gray-800">Answer Questions</p>
                    <p className="text-sm text-gray-600">Choose your answer quickly - faster correct answers earn more points!</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">5</span>
                  <div>
                    <p className="font-medium text-gray-800">Check Leaderboard</p>
                    <p className="text-sm text-gray-600">See your ranking and final score when the quiz ends</p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>

        {/* PowerPoint Add-in Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-6 text-gray-800 flex items-center gap-2">
            <span>📊</span> PowerPoint Add-in
          </h2>
          
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 mb-6">
            <h3 className="text-2xl font-semibold text-indigo-700 mb-4 flex items-center gap-2">
              <span>🚀</span> QuickQuiz for PowerPoint
            </h3>
            <p className="text-gray-700 mb-4">
              Present quizzes directly from PowerPoint with our free add-in! Control quiz flow, insert join slides with QR codes, and manage presentations without leaving PowerPoint.
            </p>
            
            <div className="bg-white rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-800 mb-3">Features:</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Present quizzes with built-in countdown timer</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Insert join slides with QR codes automatically</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Control reveal answers and show results in real-time</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Live participant tracking during presentations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>No admin permissions required to install</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={downloadUrl}
                download
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg text-center transition-colors shadow-md"
              >
                📥 Download Add-in
              </a>
              <Link
                to="/help/powerpoint"
                className="flex-1 bg-white hover:bg-gray-50 text-indigo-600 font-semibold py-3 px-6 rounded-lg text-center transition-colors border-2 border-indigo-600"
              >
                📖 Installation Guide
              </Link>
            </div>

            <div className="mt-4 text-sm text-gray-600 bg-white rounded-lg p-3">
              <strong>Version:</strong> {addinVersion} | <strong>Compatibility:</strong> PowerPoint 2016 or later | <strong>Platform:</strong> Windows
            </div>
          </div>
        </div>

        {/* Features Showcase */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-6 text-gray-800 flex items-center gap-2">
            <span>✨</span> Key Features
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="border-2 border-blue-200 rounded-xl p-5 hover:border-blue-400 transition-colors">
              <div className="text-3xl mb-3">⚡</div>
              <h3 className="font-semibold text-lg mb-2">Real-Time Updates</h3>
              <p className="text-gray-600 text-sm">Watch participants join live, see answers come in instantly, and view the leaderboard update in real-time</p>
            </div>
            <div className="border-2 border-purple-200 rounded-xl p-5 hover:border-purple-400 transition-colors">
              <div className="text-3xl mb-3">⏱️</div>
              <h3 className="font-semibold text-lg mb-2">Custom Time Limits</h3>
              <p className="text-gray-600 text-sm">Set different time limits for each question (5-120 seconds). Keep your quiz fast-paced or give time to think</p>
            </div>
            <div className="border-2 border-green-200 rounded-xl p-5 hover:border-green-400 transition-colors">
              <div className="text-3xl mb-3">📊</div>
              <h3 className="font-semibold text-lg mb-2">Download Results</h3>
              <p className="text-gray-600 text-sm">Export quiz results as CSV or PowerPoint for easy sharing, grading, or record-keeping</p>
            </div>
            <div className="border-2 border-pink-200 rounded-xl p-5 hover:border-pink-400 transition-colors">
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="font-semibold text-lg mb-2">Smart Scoring</h3>
              <p className="text-gray-600 text-sm">Points based on accuracy and speed (600-1000 per question). Faster correct answers earn more points</p>
            </div>
            <div className="border-2 border-yellow-200 rounded-xl p-5 hover:border-yellow-400 transition-colors">
              <div className="text-3xl mb-3">📱</div>
              <h3 className="font-semibold text-lg mb-2">QR Code Sharing</h3>
              <p className="text-gray-600 text-sm">Generate QR codes for instant joining. Perfect for classroom or in-person events</p>
            </div>
            <div className="border-2 border-indigo-200 rounded-xl p-5 hover:border-indigo-400 transition-colors">
              <div className="text-3xl mb-3">🎲</div>
              <h3 className="font-semibold text-lg mb-2">Random Trivia</h3>
              <p className="text-gray-600 text-sm">Generate random trivia quizzes instantly with questions from various categories</p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-6 text-gray-800 flex items-center gap-2">
            <span>❓</span> Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border-2 border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 transition-colors">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full text-left p-4 flex justify-between items-center hover:bg-gray-50"
                >
                  <span className="font-semibold text-gray-800 pr-4">{faq.question}</span>
                  <span className="text-2xl text-blue-600 flex-shrink-0">
                    {openFaq === index ? '−' : '+'}
                  </span>
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-4 pt-2 bg-gray-50 border-t-2 border-gray-200">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Tips & Best Practices */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-lg p-8 text-white mb-8">
          <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
            <span>💡</span> Tips & Best Practices
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-xl mb-3 flex items-center gap-2">
                <span>📝</span> Quiz Creation Tips
              </h3>
              <ul className="space-y-2 text-blue-50">
                <li>• Keep questions clear and concise</li>
                <li>• Use 3-4 answer options for best results</li>
                <li>• Vary difficulty to keep it engaging</li>
                <li>• Set appropriate time limits (20-30 seconds is ideal)</li>
                <li>• Test your quiz before sharing with participants</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-xl mb-3 flex items-center gap-2">
                <span>🎯</span> Hosting Tips
              </h3>
              <ul className="space-y-2 text-purple-50">
                <li>• Give participants time to join before starting</li>
                <li>• Share quiz codes multiple ways (URL, QR, text)</li>
                <li>• Monitor the participant list to see who's joined</li>
                <li>• Control the pace - don't rush through questions</li>
                <li>• Download results before ending the session</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold mb-6 text-gray-800 flex items-center gap-2">
            <span>🔧</span> Troubleshooting
          </h2>
          <div className="space-y-6">
            <div className="border-l-4 border-red-500 pl-4 py-2">
              <h3 className="font-semibold text-lg mb-2 text-red-700">Can't Join a Quiz?</h3>
              <ul className="space-y-1 text-gray-700">
                <li>✓ Double-check the quiz code is correct (case-sensitive)</li>
                <li>✓ Ensure you have a stable internet connection</li>
                <li>✓ Try refreshing the page</li>
                <li>✓ Clear browser cache if issues persist</li>
                <li>✓ Make sure the quiz hasn't been deleted by the host</li>
              </ul>
            </div>
            <div className="border-l-4 border-yellow-500 pl-4 py-2">
              <h3 className="font-semibold text-lg mb-2 text-yellow-700">Quiz Not Saving?</h3>
              <ul className="space-y-1 text-gray-700">
                <li>✓ Verify you're connected to the internet</li>
                <li>✓ Check that all required fields are filled</li>
                <li>✓ Each question needs at least 2 options</li>
                <li>✓ At least one answer must be marked correct</li>
                <li>✓ Try saving again after a few seconds</li>
              </ul>
            </div>
            <div className="border-l-4 border-blue-500 pl-4 py-2">
              <h3 className="font-semibold text-lg mb-2 text-blue-700">Leaderboard Not Showing?</h3>
              <ul className="space-y-1 text-gray-700">
                <li>✓ Leaderboard appears after participants answer questions</li>
                <li>✓ Make sure the quiz has started (not in waiting room)</li>
                <li>✓ Check that participants have submitted answers</li>
                <li>✓ Refresh the page to sync latest data</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-lg p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-lg mb-6 opacity-90">
            Ready to create your first quiz? It only takes a minute!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/host"
              className="bg-white text-purple-600 py-3 px-8 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              🎯 Create Your First Quiz
            </Link>
            <Link
              to="/"
              className="bg-purple-700 text-white py-3 px-8 rounded-lg font-semibold hover:bg-purple-800 transition-colors border-2 border-white"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
